import { fetchFacultyCourses, fetchFacultyActivities, fetchFacultyForms, FacultyCourse, FacultyActivityItem, FacultyFormItem } from './FacultyActivities';

export interface FacultyActivitiesData {
  courses: FacultyCourse[];
  activities: FacultyActivityItem[];
  forms: FacultyFormItem[];
}

// The Activities/Pre-test/Post-test tabs (both the standalone page and each
// batch's own inner tabs) all fetch the SAME full course+activity+form fan-
// out and filter it client-side afterward — cached per contact (same TTL/
// in-flight/invalidate pattern as StudentAssessments.ts's
// fetchStudentAssessments) so revisiting this page, or a batch's inner
// page, within a session doesn't re-run the whole fan-out unless something
// actually changed.
const CACHE_TTL_MS = 60_000;
const activitiesDataCache = new Map<string, { data: FacultyActivitiesData; fetchedAt: number; inFlight?: Promise<FacultyActivitiesData> }>();

const EMPTY_DATA: FacultyActivitiesData = { courses: [], activities: [], forms: [] };

// Call after creating an activity/form, publishing a form, or reviewing a
// submission, so the next load reflects it instead of a stale cached read.
export const invalidateFacultyActivitiesCache = (contactId: string) => {
  activitiesDataCache.delete(contactId);
};

export const fetchFacultyActivitiesData = async (contactId: string): Promise<FacultyActivitiesData> => {
  const cached = activitiesDataCache.get(contactId);
  if (cached?.inFlight) return cached.inFlight;
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) return cached.data;

  const request = fetchFacultyActivitiesDataUncached()
    .then((data) => {
      activitiesDataCache.set(contactId, { data, fetchedAt: Date.now() });
      return data;
    })
    .catch((error) => {
      activitiesDataCache.delete(contactId);
      throw error;
    });
  activitiesDataCache.set(contactId, { data: cached?.data ?? EMPTY_DATA, fetchedAt: cached?.fetchedAt ?? 0, inFlight: request });
  return request;
};

const fetchFacultyActivitiesDataUncached = async (): Promise<FacultyActivitiesData> => {
  const courses = await fetchFacultyCourses();

  // Multiple course-offering rows (different sections/terms) can share the
  // same underlying batch (ProgramPlanId) — getCourses returns one row per
  // offering, not one per batch. fetchFacultyActivities/fetchFacultyForms
  // both call their Salesforce endpoint keyed on ProgramPlanId alone, so
  // fanning out over every course-offering row fired the exact same
  // getActivities/getAssessmentForms call once per sibling offering. Fetch
  // once per distinct ProgramPlanId instead, then fan the same result back
  // out to every offering that shares it (each still needs its own copy,
  // stamped with its own CourseOfferingID, for the per-batch scoped view).
  const uniqueCoursesByProgramPlan = Array.from(
    new Map(courses.filter((c) => c.ProgramPlanId).map((c) => [c.ProgramPlanId, c])).values()
  );

  const [uniqueActivities, uniqueForms] = await Promise.all([
    Promise.all(uniqueCoursesByProgramPlan.map((course) => fetchFacultyActivities(course))),
    Promise.all(uniqueCoursesByProgramPlan.map((course) => fetchFacultyForms(course))),
  ]);

  const activitiesByProgramPlan = new Map(uniqueCoursesByProgramPlan.map((c, i) => [c.ProgramPlanId, uniqueActivities[i]]));
  const formsByProgramPlan = new Map(uniqueCoursesByProgramPlan.map((c, i) => [c.ProgramPlanId, uniqueForms[i]]));

  const activities = courses.flatMap((course) => {
    const items = activitiesByProgramPlan.get(course.ProgramPlanId) ?? [];
    return items.map((item) => ({ ...item, courseOfferingId: course.CourseOfferingID, batchName: course.TermName }));
  });
  const forms = courses.flatMap((course) => {
    const items = formsByProgramPlan.get(course.ProgramPlanId) ?? [];
    return items.map((item) => ({ ...item, courseOfferingId: course.CourseOfferingID, batchName: course.TermName }));
  });

  return { courses, activities, forms };
};
